import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Copy } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";

const PREFIXES = ["Get", "Try", "Use", "Go", "My", "The", "Meta", "Neo", "Nova", "Bright", "Prime", "Swift"];
const SUFFIXES = ["Hub", "ify", "ly", "io", "App", "Labs", "Base", "Kit", "Go", "Now", "Pro", "Wave", "Loop", "Box", "Spot", "Craft"];

interface Industry {
  label: string;
  prefixes: string[];
  suffixes: string[];
}

// Distinct word banks per industry -- blended with the generic ones
// rather than replacing them entirely, so results still feel varied
// while leaning toward vocabulary that actually fits the category.
const INDUSTRIES: Record<string, Industry> = {
  none: { label: "No specific industry", prefixes: [], suffixes: [] },
  tech: {
    label: "Technology & Digital",
    prefixes: ["Byte", "Cloud", "Sync", "Logic", "Data", "Code"],
    suffixes: ["Sync", "Byte", "Stack", "Node", "Script", "AI"],
  },
  food: {
    label: "Food & Hospitality",
    prefixes: ["Fresh", "Bite", "Table", "Kitchen", "Savor", "Harvest"],
    suffixes: ["Bites", "Table", "Kitchen", "Fare", "Plate", "Bowl"],
  },
  creative: {
    label: "Creative & Lifestyle",
    prefixes: ["Studio", "Muse", "Canvas", "Craft", "Bloom", "Aura"],
    suffixes: ["Studio", "Muse", "Craft", "Atelier", "Collective", "Design"],
  },
  health: {
    label: "Health & Wellness",
    prefixes: ["Vital", "Pure", "Balance", "Thrive", "Calm", "Vive"],
    suffixes: ["Well", "Vital", "Care", "Balance", "Wellness", "Fit"],
  },
  finance: {
    label: "Finance & Professional Services",
    prefixes: ["Ledger", "Capital", "Trust", "Vault", "Sage", "Prime"],
    suffixes: ["Capital", "Ledger", "Trust", "Advisory", "Partners", "Fund"],
  },
  retail: {
    label: "Retail & E-commerce",
    prefixes: ["Shop", "Market", "Bundle", "Curated", "Direct", "Nook"],
    suffixes: ["Market", "Shop", "Goods", "Store", "Mart", "Collective"],
  },
};

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function dropTrailingVowel(word: string): string {
  const vowels = "aeiouAEIOU";
  if (word.length > 3 && vowels.includes(word[word.length - 1])) {
    return word.slice(0, -1);
  }
  return word;
}

export const MAX_KEYWORD = 20;

/** Domain-safe words: accents folded, "&" read as "and", other symbols dropped, each word capped. */
export function cleanKeywordWords(keyword: string): string[] {
  return keyword
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim().split(/\s+/).filter(Boolean)
    .map(w => w.slice(0, MAX_KEYWORD));
}

export function generateNames(keyword: string, industryKey: string): string[] {
  const words = cleanKeywordWords(keyword).slice(0, 3);
  // Multi-word keywords keep every word whole and capitalised ("Pet Care" -> "PetCare").
  const clean = words.join("");
  if (!clean) return [];
  const cap = words.map(capitalize).join("");
  // Dropping a trailing vowel only reads as a brand for one longer word (Flickr-style);
  // on "Pet Care" it produced "PetCar", which just looks like a typo.
  const canTrim = words.length === 1 && clean.length >= 6;
  const trimmed = canTrim ? capitalize(dropTrailingVowel(clean)) : cap;

  const industry = INDUSTRIES[industryKey] ?? INDUSTRIES.none;
  // Filtered so an industry word that happens to match the keyword itself
  // (e.g. keyword "cloud" + the Technology industry's own "Cloud" prefix)
  // doesn't produce an awkward, redundant result like "CloudCloud".
  const industryPrefixes = industry.prefixes.filter(w => w.toLowerCase() !== clean.toLowerCase());
  const industrySuffixes = industry.suffixes.filter(w => w.toLowerCase() !== clean.toLowerCase());
  const prefixes = [...PREFIXES, ...industryPrefixes];
  const suffixes = [...SUFFIXES, ...industrySuffixes];

  const names = new Set<string>();

  // Keyword + suffix
  suffixes.forEach(suf => names.add(`${cap}${suf}`));
  // Prefix + keyword
  prefixes.forEach(pre => names.add(`${pre}${cap}`));
  // Trimmed (vowel-dropped) + suffix, classic startup style
  if (canTrim) suffixes.slice(0, 8).forEach(suf => names.add(`${trimmed}${suf}`));
  // Prefix + trimmed
  if (canTrim) prefixes.slice(0, 8).forEach(pre => names.add(`${pre}${trimmed}`));
  // Industry word combined directly with the keyword, when an industry is picked
  if (industryPrefixes.length > 0) {
    industryPrefixes.slice(0, 3).forEach(word => names.add(`${word}${cap}`));
    industrySuffixes.slice(0, 3).forEach(word => names.add(`${cap}${word}`));
  }

  return Array.from(names);
}

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const TOOL_INFO_CONTENT = {
  what: `Generates business and startup name ideas based on keywords you provide, useful as a brainstorming starting point when naming a new venture.`,
  how: `Enter a few keywords related to your business or industry, and a list of generated name suggestions appears.`,
  tip: `Before committing to any generated name, check domain availability and existing trademark registrations — a great-sounding name is only useful if you can actually secure the matching web address and legally use it.`,
};

export default function StartupNameGenerator() {
  const [keyword, setKeyword] = useState("cloud");
  const [industryKey, setIndustryKey] = useState("none");
  const [seed, setSeed] = useState(0);

  const allNames = generateNames(keyword, industryKey);
  const shuffled = shuffle(allNames);
  // seed forces a re-shuffle on "Generate More" without needing new input
  void seed;
  const names = shuffled.slice(0, 15);

  const copy = (name: string) => {
    void copyText(name, `Copied "${name}"`);
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <Label>Enter a keyword related to your business</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="e.g. cloud, fit, market" maxLength={40} aria-label="Business keyword" />
            <Button variant="outline" onClick={() => setSeed(s => s + 1)} className="shrink-0 gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Shuffle
            </Button>
          </div>
        </div>

        <div>
          <Label>Industry (optional, tailors the results)</Label>
          <Select value={industryKey} onValueChange={setIndustryKey}>
            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(INDUSTRIES).map(([key, ind]) => (
                <SelectItem key={key} value={key}>{ind.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {blocked ? (
          <p className="text-sm text-center text-muted-foreground py-4">— blocked at today's usage limit —</p>
        ) : names.length === 0 ? (
          <p role="alert" className="text-sm text-muted-foreground text-center py-4">
            {keyword.trim() ? "Use letters or numbers in your keyword (symbols and accents are removed) to see name ideas." : "Type a keyword above to see name ideas."}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {names.map(name => (
              <button
                key={name}
                onClick={() => copy(name)}
                className="p-3 rounded-lg border bg-card hover:border-primary/30 transition-colors text-sm font-medium flex items-center justify-between gap-2 group"
              >
                <span className="truncate">{name}</span>
                <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center">
          Generated names are suggestions only — always check domain and trademark availability before committing to one.
        </p>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
